import { Link } from "react-router-dom";
import { Lock, Sparkles, Key, FlaskConical, Share2 } from "lucide-react";

const PERKS = [
  { icon: FlaskConical, text: "Full Punnett-square Breeding Calculator across 13 loci" },
  { icon: Sparkles, text: "AI-written lab-notebook narratives for every chick outcome" },
  { icon: Share2, text: "Shareable breeding-pair URLs to send pairings to other breeders" },
];

export default function Paywall({ onUnlockClick }) {
  return (
    <div
      data-testid="paywall"
      className="max-w-4xl mx-auto px-6 lg:px-10 py-14 lg:py-20"
    >
      <div className="border border-border bg-card p-8 lg:p-12 relative overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.05]"
          style={{
            backgroundImage:
              "repeating-linear-gradient(0deg, transparent 0 23px, currentColor 23px 24px), repeating-linear-gradient(90deg, transparent 0 23px, currentColor 23px 24px)",
          }}
        />
        <div className="relative">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 grid place-items-center bg-primary text-primary-foreground rounded-sm">
              <Lock size={18} />
            </div>
            <div className="overline">Pro tier required</div>
          </div>

          <h1 className="mt-6 text-4xl sm:text-5xl font-black tracking-tight leading-[1.05]">
            Unlock the full{" "}
            <span className="bg-primary text-primary-foreground px-2">
              breeding lab
            </span>
          </h1>
          <p className="mt-4 text-muted-foreground max-w-2xl leading-relaxed">
            The Mutation Index and Genetics 101 primer are free for everyone.
            The Calculator, AI chick notes and shareable pair links are part of
            <span className="font-semibold text-foreground"> Aviary Lab Pro</span> —
            a one-time unlock, no subscription.
          </p>

          <ul className="mt-8 grid gap-3">
            {PERKS.map(({ icon: Icon, text }) => (
              <li
                key={text}
                data-testid={`paywall-perk-${text.slice(0, 12)}`}
                className="flex items-start gap-3 border border-border p-3 bg-background"
              >
                <div className="w-8 h-8 grid place-items-center border border-border shrink-0">
                  <Icon size={14} />
                </div>
                <span className="text-sm leading-relaxed">{text}</span>
              </li>
            ))}
          </ul>

          <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex items-baseline gap-2">
              <div className="text-4xl font-black tracking-tight">A$14.99</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                one-time · lifetime
              </div>
            </div>
            <div className="flex gap-3 sm:ml-auto">
              <Link
                to="/pro"
                data-testid="paywall-cta-buy"
                className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-3 rounded-sm hover:bg-[hsl(222,89%,42%)] transition-colors font-medium"
              >
                <Sparkles size={16} /> Unlock Pro
              </Link>
              <button
                type="button"
                onClick={onUnlockClick}
                data-testid="paywall-cta-have-key"
                className="inline-flex items-center gap-2 border border-border px-5 py-3 rounded-sm hover:bg-secondary transition-colors font-medium"
              >
                <Key size={16} /> I have a key
              </button>
            </div>
          </div>

          <div className="mt-10 pt-6 border-t border-border font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            // Free forever: Mutation Index · Genetics 101 ·{" "}
            <Link to="/encyclopedia" className="underline" data-testid="paywall-link-free">
              browse free
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
